import { STORAGE_KEY, VERIFY_STATUS } from '../constants/generic';
import { getAppObj, safe } from './utils';
import config from '../constants/config';
import { keycloakUrlMap } from '../constants/maps';
import Keycloak from 'keycloak-js';
import {
  attachKeycloakTokenExpiredHandler,
  startKeycloakSessionRefresh,
  stopKeycloakSessionRefresh
} from './keycloakRefresh';

export const KEYCLOAK_STATUS = {
  AUTH: 0,
  UNAUTH: 1,
  FAIL: 2
};

export function shouldClearCachedUser(
  initStatus,
  ownLoginPending,
  ownLoginSucceeded
) {
  const noKeycloakSession =
    initStatus === KEYCLOAK_STATUS.UNAUTH || initStatus === KEYCLOAK_STATUS.FAIL;

  return noKeycloakSession && !ownLoginPending && !ownLoginSucceeded;
}

// keycloak-js bounds kc.init() through the 3p-cookie probe that init awaits before
// #processInit (lib/keycloak.js:277). Its early return (:751) does not apply while
// silentCheckSsoRedirectUri is set below, and applyTimeoutToPromise at :796 rejects
// after messageReceiveTimeout (10000ms by default) if the probe iframe never answers.
//
// Two paths remain unbounded by owner decision, both only reachable once that probe
// has succeeded. #checkSsoSilently (:974-1006) is a bare promise that settles only
// when its iframe posts a message. And on the leg carrying the authorization code,
// #processInit exchanges it through a plain fetch (:2087-2091) - the library holds no
// AbortController at all, so nothing in it can end that wait. The axios timeout in
// store.js does not reach either one: they are the library's own requests, not ours.
// The second is the costlier one to lose - the visitor has already signed in by then.

// A one-shot signal that Keycloak init has reached a terminal state. The request
// interceptor (store.js) awaits it before it falls back to the SSO token, so an
// authenticated request fired on a fresh page load no longer leaves before the
// SSO session has been restored (or definitively failed).
//
// Every kc.init() outcome funnels through settle() below (AUTH / UNAUTH / FAIL /
// a sync throw), and settle() resolves it. The accepted unbounded silent-check
// path described above leaves this signal pending with init. The two cases where
// kc.init() never runs at all resolve it explicitly through markKcSettled():
// a brand without Keycloak
// (App.handleKeycloak bails on `!initSettings`) and a brand config that never
// loads (actions/brand.js error path). `.done` is the synchronous mirror the
// interceptor checks so its wait-free branches never touch the microtask queue.
let resolveKcSettled;

export const kcSettled = new Promise((resolve) => {
  resolveKcSettled = resolve;
});
kcSettled.done = false;

export function markKcSettled() {
  if (kcSettled.done) return;
  kcSettled.done = true;
  resolveKcSettled();
}

// ---------------------------------------------------------------------------
// [KC-DEBUG] TEMPORARY DIAGNOSTICS - delete together with the [KC-DEBUG] logs.
// Everything below only OBSERVES; it never changes the Keycloak flow.
//
// Why: keycloak-js 26.2.4 awaits #check3pCookiesSupported() BEFORE #processInit()
// (lib/keycloak.js:276-278). That check appends the `keycloak-3p-check-iframe`,
// waits for a postMessage of 'supported' / 'unsupported', and is wrapped in
// applyTimeoutToPromise(..., messageReceiveTimeout = 10000ms) which REJECTS with
// `new Error('Timeout when waiting for 3rd party check iframe message.')`.
// A reject makes initKeycloak call cb(FAIL) -> App.handleKeycloak silently hides
// the loader and /me is never called. The logs below prove or disprove exactly that.
// ---------------------------------------------------------------------------

const KC_DEBUG_DEFAULT_MESSAGE_RECEIVE_TIMEOUT_MS = 10000;
const KC_DEBUG_PROBE_TIMES_MS = [500, 1000, 2000, 4000, 6000, 8000, 9500, 10500, 12000];
const KC_DEBUG_MESSAGE_LOG_LIMIT = 40;

let kcDebugInitOutcome = 'NOT_STARTED';
let kcDebugMessagesSeen = 0;
let kcDebugMessageSpyStarted = false;

function kcDebugCountIframes(title) {
  return safe(() => document.querySelectorAll('iframe[title="' + title + '"]').length);
}

function kcDebugLocalStorageKeys() {
  return safe(() => Object.keys(localStorage)) || [];
}

// Only the LENGTH of the authorization code is ever produced here - never the code.
function kcDebugHashCodeLength() {
  const hash = safe(() => window.location.hash) || '';
  const match = /[#&]code=([^&]*)/.exec(hash);

  return match ? match[1].length : 0;
}

// Flat primitives ONLY: a nested object prints as "Object" in a remote console.
export function kcDebugSnapshot() {
  const kc = getAppObj().keycloak || {};
  const keys = kcDebugLocalStorageKeys();
  const hash = safe(() => window.location.hash) || '';

  return {
    kcDidInitialize: kc.didInitialize,
    kcAuthenticated: kc.authenticated,
    kcHasToken: Boolean(kc.token),
    kcTokenLength: kc.token?.length,
    kcHasRefreshToken: Boolean(kc.refreshToken),
    kcHasIdToken: Boolean(kc.idToken),
    kcHasSubject: Boolean(kc.subject),
    kcRealm: kc.realm,
    kcClientId: kc.clientId,
    kcAuthServerUrl: kc.authServerUrl,
    kc3pCookiesIframeUrl: safe(() => kc.endpoints.thirdPartyCookiesIframe()),
    kcCheckSessionIframeUrl: safe(() => kc.endpoints.checkSessionIframe()),
    hashLength: hash.length,
    hashHasCode: hash.indexOf('code=') !== -1,
    hashCodeLength: kcDebugHashCodeLength(),
    hashHasState: hash.indexOf('state=') !== -1,
    hashHasError: hash.indexOf('error=') !== -1,
    iframe3pCheck: kcDebugCountIframes('keycloak-3p-check-iframe'),
    // Expected 0 since checkLoginIframe was turned off - that zero IS the evidence the
    // change took effect in whatever build is being read. Anything else means the flag
    // did not reach init().
    iframeSession: kcDebugCountIframes('keycloak-session-iframe'),
    iframeSilentCheckSso: kcDebugCountIframes('keycloak-silent-check-sso'),
    iframeTotal: safe(() => document.querySelectorAll('iframe').length),
    lsKeyCount: keys.length,
    lsKeys: keys.join(','),
    lsKcCallbackCount: keys.filter((k) => k.indexOf('kc-callback-') === 0).length,
    lsHasKeycloakConfig: keys.indexOf(STORAGE_KEY.KEYCLOAK_CONFIG) !== -1,
    lsHasLoginPending: keys.indexOf(STORAGE_KEY.LOGIN_PENDING) !== -1,
    lsHasLogoutPending: keys.indexOf(STORAGE_KEY.LOGOUT_PENDING) !== -1,
    cookieCount: safe(() => (document.cookie ? document.cookie.split(';').length : 0)),
    postMessagesSeen: kcDebugMessagesSeen,
    initOutcome: kcDebugInitOutcome,
    origin: window.location.origin
  };
}

// The S3 brand `keycloak` block is plain config (no secrets), but it prints as a bare
// "Object" in a remote console - so flatten it to primitives. Shared by every log that
// needs to show that block: actions/brand.js, reducers/brand.js.
export function kcDebugFlattenBrandKeycloak(keycloakBlock, prefix = 'brandKc') {
  const block = keycloakBlock || {};

  return {
    [prefix + 'Present']: Boolean(keycloakBlock),
    [prefix + 'Keys']: keycloakBlock ? Object.keys(block).join(',') : null,
    [prefix + 'Json']: safe(() => JSON.stringify(keycloakBlock)),
    [prefix + 'Enabled']: block.enabled,
    [prefix + 'EnabledType']: typeof block.enabled,
    [prefix + 'ClientId']: block.clientId,
    [prefix + 'Realm']: block.realm,
    [prefix + 'KeycloakBaseUrl']: block.keycloakBaseUrl,
    [prefix + 'IdentityProvider']: block.identityProvider,
    [prefix + 'UseBrandConfig']: block.useBrandConfig,
    [prefix + 'UseCompanyFlags']: block.useCompanyFlags,
    [prefix + 'AllowRegisterWithInvitationCode']: block.allowRegisterWithInvitationCode,
    [prefix + 'ButtonText']: block.buttonText
  };
}

// Passive listener: the 3p-cookie check resolves ONLY when one of these arrives.
// Silence here + a ~10000ms reject == the timeout diagnosis confirmed.
function kcDebugStartMessageSpy(t0) {
  if (kcDebugMessageSpyStarted) return;
  kcDebugMessageSpyStarted = true;

  safe(() =>
    window.addEventListener(
      'message',
      (event) => {
        kcDebugMessagesSeen += 1;

        if (kcDebugMessagesSeen > KC_DEBUG_MESSAGE_LOG_LIMIT) return;

        const data = event.data;
        const isString = typeof data === 'string';

        // SHAPE ONLY, never the payload: the 3p-check iframe answers with the literal
        // strings 'supported' / 'unsupported'; any other postMessage may carry data.
        console.log('[KC-DEBUG] keycloak.js/messageSpy: postMessage received on window (the 3p-cookie check inside kc.init() is waiting for exactly one of these)', { seq: kcDebugMessagesSeen, atMs: Math.round(performance.now() - t0), origin: event.origin, dataType: typeof data, dataLength: isString ? data.length : undefined, isSupported: data === 'supported', isUnsupported: data === 'unsupported' }); // eslint-disable-line no-console
      },
      false
    )
  );
}

// Timeline around kc.init(): watch iframe3pCheck stay at 1 while the check hangs.
function kcDebugStartProbes(t0) {
  KC_DEBUG_PROBE_TIMES_MS.forEach((atMs) => {
    setTimeout(() => {
      console.log('[KC-DEBUG] keycloak.js/probe@' + atMs + 'ms: timeline snapshot around kc.init() (iframe3pCheck=1 means the 3p-cookie iframe is STILL hanging)', { plannedAtMs: atMs, elapsedMs: Math.round(performance.now() - t0), ...kcDebugSnapshot() }); // eslint-disable-line no-console
    }, atMs);
  });
}

// These callbacks are unused by the app, so attaching them replaces no behaviour.
function kcDebugAttachClientObservers(kc, t0) {
  const at = () => Math.round(performance.now() - t0);

  kc.onReady = (authenticated) => {
    console.log('[KC-DEBUG] keycloak.js/onReady: #processInit FINISHED (this line is NEVER printed when the 3p-cookie check rejects first)', { atMs: at(), authenticated, ...kcDebugSnapshot() }); // eslint-disable-line no-console
  };

  kc.onAuthSuccess = () => {
    console.log('[KC-DEBUG] keycloak.js/onAuthSuccess: KC established a session (token exchanged)', { atMs: at(), ...kcDebugSnapshot() }); // eslint-disable-line no-console
  };

  kc.onAuthError = (errorData) => {
    console.log('[KC-DEBUG] keycloak.js/onAuthError: KC could not establish a session', { atMs: at(), error: errorData?.error, errorDescription: errorData?.error_description, errorString: String(errorData), errorJson: safe(() => JSON.stringify(errorData)) }); // eslint-disable-line no-console
  };

  kc.onAuthRefreshSuccess = () => {
    console.log('[KC-DEBUG] keycloak.js/onAuthRefreshSuccess: token refreshed', { atMs: at(), kcHasToken: Boolean(kc.token), kcTokenLength: kc.token?.length }); // eslint-disable-line no-console
  };

  kc.onAuthRefreshError = () => {
    console.log('[KC-DEBUG] keycloak.js/onAuthRefreshError: token refresh FAILED', { atMs: at(), kcAuthenticated: kc.authenticated }); // eslint-disable-line no-console
  };

  kc.onAuthLogout = () => {
    console.log('[KC-DEBUG] keycloak.js/onAuthLogout: KC reported a logout', { atMs: at(), ...kcDebugSnapshot() }); // eslint-disable-line no-console
  };
}

export function getKeycloakUrl(env = config.env) {
  console.log('[KC-DEBUG] keycloak.js/getKeycloakUrl: resolving KC base url', { env, configEnv: config.env, mapped: keycloakUrlMap[env], fellBackToProd: !keycloakUrlMap[env], resolved: keycloakUrlMap[env] || keycloakUrlMap.prod }); // eslint-disable-line no-console
  console.log('[KC-DEBUG] keycloak.js/getKeycloakUrl: operand types (env comes from window.APP.settings.env via constants/config; an unsubstituted {{env}} template lands in the prod fallback)', { envType: typeof env, envRaw: JSON.stringify(env), appSettingsEnv: getAppObj().settings?.env, appSettingsEnvType: typeof getAppObj().settings?.env, knownEnvKeys: Object.keys(keycloakUrlMap).join(','), prodFallbackUrl: keycloakUrlMap.prod }); // eslint-disable-line no-console
  return keycloakUrlMap[env] || keycloakUrlMap.prod;
}

export function initKeycloak(settings, cb) {
  const { AUTH, UNAUTH, FAIL } = KEYCLOAK_STATUS;

  // checkLoginIframe is OFF deliberately. With it on, keycloak-js polls Keycloak's
  // check-session iframe on a timer (we passed 10s) and its handler treats 'error'
  // EXACTLY like 'changed' - lib/keycloak.js:911-913, `if (event.data !== 'unchanged')
  // this.clearToken()`. A network blip, a frame the browser declines to load and a
  // KC-side /init failure are therefore indistinguishable from a real logout. What one
  // such poll costs: clearToken() drops the token and sets `authenticated = false`; our
  // request interceptor reads the token straight off the client (store.js:57), so the
  // next request leaves unauthenticated; the reactive refresh that would have repaired
  // it is gated on `keycloak?.authenticated` (store.js:71) and does not even try; and
  // the visitor gets `error.session.expired` plus a bounce home (actions/user.js:187-192).
  //
  // Three couplings the option name does not suggest (line numbers = lib/keycloak.js):
  //   - updateToken() awaits #checkLoginIframe() BEFORE it refreshes (:1465-1467), so all
  //     three of our refresh routes - onTokenExpired below, the 401/403 retry at
  //     store.js:74, the hourly one at App.js:326 - run through this iframe, and an
  //     'error' rejects them (:919-920) before any refresh is attempted.
  //   - a cold check-sso load awaits it too (:835-841); an 'error' there fails init()
  //     outright and silent-check-sso is never tried, so a visitor who does hold a live
  //     session is shown the anonymous site.
  //   - the postMessage goes out only when callbackList.length === 1 (:963). If a reply
  //     never arrives at all, the list only grows and no further message is ever sent -
  //     updateToken() then hangs forever, with nothing timing it out.
  //
  // What we give up: learning within ~10s that the session was ended in ANOTHER TAB,
  // instead of at the next refresh or the next 401. That costs nothing on screen today -
  // every Keycloak callback we attach (onReady, onAuthSuccess, onAuthError,
  // onAuthRefreshSuccess, onAuthRefreshError, onAuthLogout - :216-238 of this file) only
  // writes a [KC-DEBUG] log. And a session that genuinely ended is caught regardless: the
  // refresh grant answers 400 invalid_grant and keycloak-js clears the token itself
  // (:1507-1509).
  //
  // What this does NOT buy, so nobody plans around it:
  //   - the 3p-cookie probe still runs. Its early return (:751) is a conjunction over
  //     `!loginIframe.enable && !silentCheckSsoRedirectUri`, and we still pass the second.
  //   - the CSP surface is unchanged. One frame fewer, but on the same KC origin, and CSP
  //     does not distinguish two frames on one host.
  //   - the accepted unbounded #checkSsoSilently path (:974-1006) remains; this
  //     flag does not reach it.
  //
  // checkLoginIframeInterval went with it: its only reader is #scheduleCheckIframe
  // (:1180-1189), which now returns immediately on `!enable`.
  //
  // Extracted to a const ONLY so the logged options are literally the ones passed
  // to init() below - the values are unchanged.
  const initOptions = {
    onLoad: 'check-sso',
    silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
    checkLoginIframe: false
  };

  console.log('[KC-DEBUG] keycloak.js/initKeycloak: ENTER - about to construct Keycloak client', { url: settings?.url, realm: settings?.realm, clientId: settings?.clientId, hasUrl: Boolean(settings?.url), hasRealm: Boolean(settings?.realm), hasClientId: Boolean(settings?.clientId), origin: window.location.origin }); // eslint-disable-line no-console
  console.log('[KC-DEBUG] keycloak.js/initKeycloak: ENTER timestamp + `settings` exactly as received', { atIso: new Date().toISOString(), perfNowMs: Math.round(performance.now()), settingsType: typeof settings, settingsKeys: settings ? Object.keys(settings).join(',') : null, settingsJson: safe(() => JSON.stringify(settings)), appSettingsEnv: getAppObj().settings?.env, configEnv: config.env, origin: window.location.origin, pathname: window.location.pathname }); // eslint-disable-line no-console

  // Every reported outcome funnels through settle(): first one wins, later ones
  // are logged and ignored.
  let settled = false;

  const settle = (status, error) => {
    const statusName = { 0: 'AUTH', 1: 'UNAUTH', 2: 'FAIL' }[status];

    if (settled) {
      console.log('[KC-DEBUG] keycloak.js/initKeycloak: LATE OUTCOME IGNORED >>> status=' + statusName + ' error=' + String(error?.message) + ' <<< an earlier outcome (watchdog/init) already settled and called cb', { status, statusName, errorMessage: error?.message, atIso: new Date().toISOString() }); // eslint-disable-line no-console
      return;
    }
    settled = true;
    // Release the request interceptor the instant the outcome is known - before cb
    // acts on it - so the /me it triggers reads the just-restored token, and any
    // request already waiting on the SSO fallback resumes at once.
    markKcSettled();

    // FAIL is what the app acts on: redux concludes "no session", the UI goes
    // anonymous. A failed client must not keep feeding tokens into the app
    // through the proactive-refresh handler below - saveSsoTokens would arm the
    // request interceptor while noTokensFound() keeps reporting an anonymous user.
    if (status === FAIL) {
      safe(() => {
        getAppObj().keycloak.onTokenExpired = null;
      });
      stopKeycloakSessionRefresh();
    }

    cb(status, error);
  };

  try {
    const t0 = performance.now();

    kcDebugStartMessageSpy(t0);
    getAppObj().keycloak = new Keycloak(settings);

    console.log('[KC-DEBUG] keycloak.js/initKeycloak: Keycloak client CONSTRUCTED (endpoints not resolved yet - #loadConfig runs inside init)', { t0Ms: Math.round(t0), ...kcDebugSnapshot() }); // eslint-disable-line no-console

    kcDebugAttachClientObservers(getAppObj().keycloak, t0);

    attachKeycloakTokenExpiredHandler(getAppObj().keycloak);

    kcDebugInitOutcome = 'PENDING';
    kcDebugStartProbes(t0);

    console.log('[KC-DEBUG] keycloak.js/initKeycloak: CALLING kc.init() NOW - EXACT options passed (no messageReceiveTimeout is passed, so keycloak-js uses its 10000ms default for the 3p-cookie check)', { optOnLoad: initOptions.onLoad, optSilentCheckSsoRedirectUri: initOptions.silentCheckSsoRedirectUri, optCheckLoginIframe: initOptions.checkLoginIframe, optKeys: Object.keys(initOptions).join(','), optJson: safe(() => JSON.stringify(initOptions)), messageReceiveTimeoutPassed: 'messageReceiveTimeout' in initOptions, effectiveMessageReceiveTimeoutMs: KC_DEBUG_DEFAULT_MESSAGE_RECEIVE_TIMEOUT_MS, silentCheckSsoFallbackPassed: 'silentCheckSsoFallback' in initOptions, t0Ms: Math.round(t0), atIso: new Date().toISOString() }); // eslint-disable-line no-console

    console.log('[KC-DEBUG] keycloak.js/initKeycloak: NO APP WATCHDOG - the keycloak-js 3p-cookie probe rejects after its messageReceiveTimeout (10000ms by default); after a successful probe, silent-check-sso remains unbounded by owner decision', { defaultMessageReceiveTimeoutMs: KC_DEBUG_DEFAULT_MESSAGE_RECEIVE_TIMEOUT_MS, t0Ms: Math.round(t0), atIso: new Date().toISOString() }); // eslint-disable-line no-console

    getAppObj()
      .keycloak.init(initOptions)
      .then((authenticated) => {
        const tookMs = Math.round(performance.now() - t0);

        kcDebugInitOutcome = authenticated ? 'RESOLVED_AUTH' : 'RESOLVED_UNAUTH';

        console.log('[KC-DEBUG] keycloak.js/initKeycloak: kc.init() RESOLVED', { authenticated, status: authenticated ? 'AUTH' : 'UNAUTH', hasToken: Boolean(getAppObj().keycloak?.token), tokenLength: getAppObj().keycloak?.token?.length, tokenExp: getAppObj().keycloak?.tokenParsed?.exp, hasRefreshToken: Boolean(getAppObj().keycloak?.refreshToken), subjectPresent: Boolean(getAppObj().keycloak?.subject) }); // eslint-disable-line no-console
        console.log('[KC-DEBUG] keycloak.js/initKeycloak: kc.init() SETTLED=RESOLVE after ' + tookMs + 'ms (>=9500ms would mean the 3p-cookie check still ate 10s before succeeding)', { tookMs, authenticated, settledAs: 'RESOLVE', looksLike3pCookieTimeout: tookMs >= 9500, defaultMessageReceiveTimeoutMs: KC_DEBUG_DEFAULT_MESSAGE_RECEIVE_TIMEOUT_MS, postMessagesSeen: kcDebugMessagesSeen, atIso: new Date().toISOString() }); // eslint-disable-line no-console
        console.log('[KC-DEBUG] keycloak.js/initKeycloak: STATE SNAPSHOT AT RESOLVE', kcDebugSnapshot()); // eslint-disable-line no-console
        if (authenticated) {
          startKeycloakSessionRefresh(getAppObj().keycloak);
        } else {
          stopKeycloakSessionRefresh();
        }
        settle(authenticated ? AUTH : UNAUTH);
      })
      .catch((e) => {
        const tookMs = Math.round(performance.now() - t0);
        const message = String(e?.message || '');

        kcDebugInitOutcome = 'REJECTED';

        console.log('[KC-DEBUG] keycloak.js/initKeycloak: kc.init() REJECTED -> FAIL (no /me will follow)', { error: String(e), errorMessage: e?.message }); // eslint-disable-line no-console
        console.log('[KC-DEBUG] keycloak.js/initKeycloak: kc.init() SETTLED=REJECT after ' + tookMs + 'ms - A VALUE NEAR 10000ms CONFIRMS THE 3rd-PARTY-COOKIE CHECK TIMEOUT', { tookMs, settledAs: 'REJECT', looksLike3pCookieTimeout: tookMs >= 9500 && tookMs <= 11500, defaultMessageReceiveTimeoutMs: KC_DEBUG_DEFAULT_MESSAGE_RECEIVE_TIMEOUT_MS, postMessagesSeenBeforeReject: kcDebugMessagesSeen, atIso: new Date().toISOString() }); // eslint-disable-line no-console
        // The message is inlined into the log STRING on purpose: an object payload can
        // collapse to a bare "Object" depending on how the console is read.
        console.log('[KC-DEBUG] keycloak.js/initKeycloak: REJECT REASON VERBATIM >>> ' + String(e?.message) + ' <<< (expected if the diagnosis holds: "Timeout when waiting for 3rd party check iframe message.")', { errorMessage: e?.message, errorName: e?.name, errorString: String(e), errorJson: safe(() => JSON.stringify(e)), errorType: typeof e, isErrorInstance: e instanceof Error, errorOwnKeys: safe(() => Object.getOwnPropertyNames(Object(e)).join(',')), errorEnumerableKeys: safe(() => Object.keys(Object(e)).join(',')), errorStackTop: String(e?.stack || '').split('\n')[0], is3pCookieTimeout: message.indexOf('3rd party check iframe') !== -1, isInitTwice: message.indexOf('only be initialized once') !== -1 }); // eslint-disable-line no-console
        console.log('[KC-DEBUG] keycloak.js/initKeycloak: REJECT VERDICT >>> ' + (message.indexOf('3rd party check iframe') !== -1 ? 'CONFIRMED: 3rd-party-cookie check timed out' : 'NOT the 3p-cookie timeout - read the verbatim reason above') + ' <<< settled in ' + tookMs + 'ms, postMessages seen: ' + kcDebugMessagesSeen); // eslint-disable-line no-console
        console.log('[KC-DEBUG] keycloak.js/initKeycloak: REJECT `error` fields kept SEPARATE (keycloak-js can reject with a plain {error, error_description} instead of an Error)', { error: e?.error, errorDescription: e?.error_description, hasErrorField: safe(() => 'error' in Object(e)), hasErrorDescriptionField: safe(() => 'error_description' in Object(e)) }); // eslint-disable-line no-console
        console.log('[KC-DEBUG] keycloak.js/initKeycloak: REJECT raw error object ->', e); // eslint-disable-line no-console
        console.log('[KC-DEBUG] keycloak.js/initKeycloak: STATE SNAPSHOT AT REJECT', kcDebugSnapshot()); // eslint-disable-line no-console
        settle(FAIL, e);
      });
  } catch (e) {
    const message = String(e?.message || '');

    kcDebugInitOutcome = 'THREW_SYNC';

    console.log('[KC-DEBUG] keycloak.js/initKeycloak: THREW synchronously -> FAIL (no /me will follow)', { error: String(e), errorMessage: e?.message }); // eslint-disable-line no-console
    console.log('[KC-DEBUG] keycloak.js/initKeycloak: SYNC THROW detail (a construction/config problem, NOT the 3p-cookie timeout)', { errorMessage: e?.message, errorName: e?.name, errorString: String(e), errorJson: safe(() => JSON.stringify(e)), errorType: typeof e, isErrorInstance: e instanceof Error, errorStackTop: String(e?.stack || '').split('\n')[0], isInitTwice: message.indexOf('only be initialized once') !== -1 }); // eslint-disable-line no-console
    console.log('[KC-DEBUG] keycloak.js/initKeycloak: STATE SNAPSHOT AT SYNC THROW', kcDebugSnapshot()); // eslint-disable-line no-console
    // Through settle (not cb directly) so the same one-shot terminal path releases
    // requests and performs FAIL cleanup.
    settle(FAIL, e);
  }
}

export function isLoginPending() {
  const value = safe(() => localStorage.getItem(STORAGE_KEY.LOGIN_PENDING));

  console.log('[KC-DEBUG] keycloak.js/isLoginPending: READ localStorage LOGIN_PENDING', { hasValue: Boolean(value), valueType: typeof value, value: String(value) }); // eslint-disable-line no-console
  return localStorage.getItem(STORAGE_KEY.LOGIN_PENDING);
}

export function setLoginPending() {
  console.log('[KC-DEBUG] keycloak.js/setLoginPending: WRITE localStorage LOGIN_PENDING=true (set right before leaving for the KC login page)'); // eslint-disable-line no-console
  localStorage.setItem(STORAGE_KEY.LOGIN_PENDING, true);
}

export function clearLoginPending() {
  console.log('[KC-DEBUG] keycloak.js/clearLoginPending: REMOVE localStorage LOGIN_PENDING', { wasSet: Boolean(safe(() => localStorage.getItem(STORAGE_KEY.LOGIN_PENDING))) }); // eslint-disable-line no-console
  localStorage.removeItem(STORAGE_KEY.LOGIN_PENDING);
}

export function isLogoutPending() {
  const value = safe(() => localStorage.getItem(STORAGE_KEY.LOGOUT_PENDING));

  console.log('[KC-DEBUG] keycloak.js/isLogoutPending: READ localStorage LOGOUT_PENDING (truthy here makes handleKeycloak finalise a logout instead of calling /me)', { hasValue: Boolean(value), valueType: typeof value, value: String(value) }); // eslint-disable-line no-console
  return localStorage.getItem(STORAGE_KEY.LOGOUT_PENDING);
}

export function setLogoutPending() {
  console.log('[KC-DEBUG] keycloak.js/setLogoutPending: WRITE localStorage LOGOUT_PENDING=true'); // eslint-disable-line no-console
  localStorage.setItem(STORAGE_KEY.LOGOUT_PENDING, true);
}

export function clearLogoutPending() {
  console.log('[KC-DEBUG] keycloak.js/clearLogoutPending: REMOVE localStorage LOGOUT_PENDING', { wasSet: Boolean(safe(() => localStorage.getItem(STORAGE_KEY.LOGOUT_PENDING))) }); // eslint-disable-line no-console
  localStorage.removeItem(STORAGE_KEY.LOGOUT_PENDING);
}

// "The registration guard has already sent this visitor to the Keycloak login page
// from THIS route." It has to survive a full-page round trip, hence storage;
// sessionStorage so it dies with the tab.
//
// It stores the route rather than a bare `true` because the loop it exists to break
// is per-URL: cancel the login, press Back, the effect re-runs and sends him straight
// out again. A boolean would additionally spend the whole tab - a visitor who backs
// out never signs in, so he is never `allowed` and never logs out, and nothing would
// clear it; every later invite link opened in that tab would go to the fallback.
//
// LOGIN_PENDING cannot serve here - nothing clears it when a round trip ends without
// a session, so one login abandoned anywhere in the app would block the guard.
export function wasSentToKeycloakLogin(route) {
  return safe(() => sessionStorage.getItem(STORAGE_KEY.REGISTER_LOGIN_SENT)) === route;
}

export function markSentToKeycloakLogin(route) {
  safe(() => sessionStorage.setItem(STORAGE_KEY.REGISTER_LOGIN_SENT, route));
}

// A logout ends whatever round trip the marker describes and keeps the same tab, so
// the same invite link must be able to start a new login afterwards.
export function clearSentToKeycloakLogin() {
  safe(() => sessionStorage.removeItem(STORAGE_KEY.REGISTER_LOGIN_SENT));
}

// Does this brand have a Keycloak at all? Truthiness of the config does not answer it:
// a brand without Keycloak still gets one, degraded by parseKeycloakConfig to
// `{buttonText}`. `initSettings` is the part that is filled in only for a brand with a
// real client, and it is the exact condition under which App.handleKeycloak calls
// initKeycloak - so when it is missing, kc.init() never runs and never answers.
export function brandHasKeycloak(keycloakConfig) {
  return Boolean(keycloakConfig?.initSettings);
}

// Has the app finished deciding whether this visitor is signed in? Everything guarding
// a route needs that question answered, and no single flag answers it: an empty user
// slice is equally true before Keycloak has spoken and after it has said "nobody".
//
// It is deliberately NOT `initStatus === null` on its own. initStatus is written from
// exactly one place - the initKeycloak callback in App.handleKeycloak - so every path
// that never reaches init() leaves it null for the rest of the page, and a bare null
// check would hold those pages open forever:
//   - A brand with no Keycloak. handleKeycloak bails on `!initSettings` before init and
//     only marks kcSettled. `initSettings` is exactly what brandHasKeycloak reads, so
//     that bail and the term below cannot drift apart.
//   - A brand config that never loaded. configFetched stays false, so handleTokens
//     never calls handleKeycloak at all - and App.render mounts no routes on such a
//     page either, so nothing that reads this is ever on screen there.
//   - The one-shot `getAppObj().keycloakInit` guard, which nothing resets. It can only
//     swallow a SECOND handleKeycloak, and what the first one ruled on is frozen from
//     the moment configFetched flips: keycloakConfigSelector derives from the S3 brand
//     block and the env alone, and the later brand writes (contract merges, LOGOUT)
//     carry that block through untouched. So it cannot turn a bail into an init that
//     was owed and never ran.
//   - A visitor authenticated without Keycloak at all: a legacy or impersonation token,
//     which the request interceptor honours with no wait (store.js:51-54), or a user
//     slice rehydrated from sessionStorage on F5. Callers ask "is he already in" first,
//     so none of those waits on an init that is not coming.
// Once init() reports an outcome, its resolve, reject or synchronous throw funnels
// through settle(), which always calls the callback. The accepted unbounded silent
// check described above reports no outcome and therefore keeps this decision pending.
//
// The second term is the leg after Keycloak. A session it found says nothing until GET
// /me says who is behind it, and PENDING is the one status meaning "asked, no answer
// yet" - it is only ever reached from AUTH. Without it a signed-in visitor is briefly
// indistinguishable from an anonymous one and gets bounced home with a session-expired
// flash. AUTH with IDLE is NOT a wait: the only path that stops there is
// handleKeycloak's logoutPending branch, which never asks /me at all.
export function isAuthDecisionPending(
  keycloakConfig,
  initStatus,
  verifyStatus,
  ownLoginPending
) {
  return (
    ownLoginPending ||
    (brandHasKeycloak(keycloakConfig) &&
      (initStatus === null || verifyStatus === VERIFY_STATUS.PENDING))
  );
}

// Can we actually start a Keycloak login right now? On top of the brand having one:
// the client object is assigned before init() runs, so `createLoginUrl` exists from the
// first moment, while `endpoints` - which it reads (`endpoints.authorize()`) - is only
// assigned once init()'s own loadConfig() has resolved the OIDC endpoints. So this says
// "the config was accepted and the login URL can be built", not "init has finished":
// loadConfig resolves before the 3rd-party-cookie check that a FAIL usually comes from,
// so endpoints are normally there even after a FAIL. What it does rule out is calling
// createLoginUrl before init, or after a config that never loaded - both would throw.
export function canUseKeycloakLogin(keycloakConfig) {
  const { keycloak } = getAppObj();

  return (
    brandHasKeycloak(keycloakConfig) &&
    Boolean(keycloak?.createLoginUrl && keycloak?.endpoints)
  );
}

export function getKeycloakEmail() {
  console.log('[KC-DEBUG] keycloak.js/getKeycloakEmail: reading email from parsed id token', { hasIdTokenParsed: Boolean(getAppObj().keycloak?.idTokenParsed), hasEmail: Boolean(getAppObj().keycloak?.idTokenParsed?.email), emailDomain: getAppObj().keycloak?.idTokenParsed?.email?.split('@')[1] }); // eslint-disable-line no-console
  return getAppObj().keycloak?.idTokenParsed?.email;
}
